// SPDX-License-Identifier: AGPL-3.0-or-later
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';

const logger = new Logger('IAR');

type ReportKind = 'message' | 'user' | 'guild';

interface BaseReportBody {
	category: string;
	additional_info?: string;
}

function optionalText(value: string | undefined): string | undefined {
	return value || undefined;
}

async function submitReport(endpoint: string, body: Record<string, unknown>): Promise<void> {
	await conduit.post(endpoint, {body});
}

function rethrowReportFailure(kind: ReportKind, error: unknown): never {
	logger.error(`Failed to submit ${kind} report:`, error);
	throw error;
}

function baseReportBody(category: string, additionalInfo?: string): BaseReportBody {
	return {
		category,
		additional_info: optionalText(additionalInfo),
	};
}

export async function reportMessage(
	channelId: string,
	messageId: string,
	category: string,
	additionalInfo?: string,
): Promise<void> {
	try {
		logger.debug(`Reporting message ${messageId} in channel ${channelId}`);
		await submitReport(Endpoints.REPORT_MESSAGE, {
			...baseReportBody(category, additionalInfo),
			channel_id: channelId,
			message_id: messageId,
		});
		logger.info('Message report submitted successfully');
	} catch (error) {
		rethrowReportFailure('message', error);
	}
}

export async function reportUser(
	userId: string,
	category: string,
	additionalInfo?: string,
	guildId?: string,
): Promise<void> {
	try {
		logger.debug(`Reporting user ${userId}${guildId ? ` in guild ${guildId}` : ''}`);
		await submitReport(Endpoints.REPORT_USER, {
			...baseReportBody(category, additionalInfo),
			user_id: userId,
			guild_id: optionalText(guildId),
		});
		logger.info('User report submitted successfully');
	} catch (error) {
		rethrowReportFailure('user', error);
	}
}

export async function reportGuild(guildId: string, category: string, additionalInfo?: string): Promise<void> {
	try {
		logger.debug(`Reporting guild ${guildId}`);
		await submitReport(Endpoints.REPORT_GUILD, {
			...baseReportBody(category, additionalInfo),
			guild_id: guildId,
		});
		logger.info('Guild report submitted successfully');
	} catch (error) {
		rethrowReportFailure('guild', error);
	}
}
